/**
 * DuplicateInlineHint — kleine, elegante Toast-Meldung, die erscheint, wenn
 * ein Inline-Edit oder Research eine E-Mail auf einen Kontakt schreibt, die
 * bereits an einem anderen, nicht-gemergten Kontakt existiert.
 *
 * Design-Prinzipien:
 *   - Nicht-blockierend: klein, unten rechts, kein Overlay.
 *   - Sofortiges Feedback (Client-Query nach Save) — der Server-Trigger
 *     erzeugt zusätzlich einen persistenten Alert, aber der Toast ist der
 *     unmittelbare, kontextuelle Hinweis.
 *   - Zwei klare Aktionen:
 *       • "Öffnen"          → springt zum kollidierenden Kontakt
 *       • "Zusammenführen"  → öffnet den Merge-Modal für die 2 Kontakte
 *   - Auto-dismiss nach 12 s; manuell schließbar mit ×.
 *
 * Keine externen Deps außer React — CSS ist inline gehalten, damit der Hint
 * überall gerendert werden kann, ohne globale Styles zu belasten.
 */

import React, { useEffect } from 'react';
import { hintAnim } from './modalAnimations';

const AUTO_DISMISS_MS = 12000;

export default function DuplicateInlineHint({ hint, onOpen, onMerge, onDismiss }) {
  useEffect(() => {
    if (!hint) return undefined;
    const t = setTimeout(() => onDismiss && onDismiss(), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [hint, onDismiss]);

  if (!hint) return null;
  const { email, matches } = hint;
  if (!Array.isArray(matches) || matches.length === 0) return null;

  const first = matches[0];
  const extra = matches.length > 1 ? matches.length - 1 : 0;

  return (
    <div style={styles.wrap} role="status" aria-live="polite">
      <div style={styles.accent} />
      <div style={styles.body}>
        <div style={styles.header}>
          <span style={styles.icon} aria-hidden>⚠</span>
          <span style={styles.title}>Email already exists</span>
          <button
            type="button"
            onClick={onDismiss}
            style={styles.close}
            aria-label="Close"
          >×</button>
        </div>
        <div style={styles.line}>
          <span style={styles.email}>{email}</span>
          <span style={styles.sep}> → </span>
          <span style={styles.contact}>{first.businessName || '(no name)'}</span>
          {extra > 0 && (
            <span style={styles.more}> +{extra} more</span>
          )}
        </div>
        <div style={styles.actions}>
          <button type="button" onClick={() => onOpen && onOpen(first.id)} style={btnGhost}>
            Open
          </button>
          <button type="button" onClick={onMerge} style={btnPrimary}>
            Merge…
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    position: 'fixed',
    right: 20,
    bottom: 20,
    zIndex: 10002,
    display: 'flex',
    minWidth: 320,
    maxWidth: 380,
    background: '#ffffff',
    borderRadius: 10,
    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16), 0 2px 6px rgba(15, 23, 42, 0.08)',
    overflow: 'hidden',
    animation: hintAnim,
  },
  accent: {
    width: 4,
    background: '#f59e0b',
    flexShrink: 0,
  },
  body: {
    flex: 1,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  icon: {
    fontSize: 14,
    color: '#f59e0b',
    lineHeight: 1,
  },
  title: {
    flex: 1,
    fontSize: 13,
    fontWeight: 600,
    color: '#0f172a',
    letterSpacing: 0.1,
  },
  close: {
    background: 'transparent',
    border: 'none',
    fontSize: 18,
    lineHeight: 1,
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '0 2px',
  },
  line: {
    fontSize: 12,
    color: '#475569',
    lineHeight: 1.4,
    wordBreak: 'break-word',
  },
  email: {
    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
    color: '#0f172a',
    fontSize: 11.5,
    background: '#f1f5f9',
    padding: '1px 6px',
    borderRadius: 4,
  },
  sep: { color: '#94a3b8', margin: '0 2px' },
  contact: { color: '#0f172a', fontWeight: 600 },
  more: { color: '#94a3b8', marginLeft: 6, fontSize: 11 },
  actions: {
    display: 'flex',
    gap: 6,
    marginTop: 2,
  },
};

const btnBase = {
  fontSize: 12,
  fontWeight: 600,
  padding: '6px 12px',
  borderRadius: 6,
  border: 'none',
  cursor: 'pointer',
  lineHeight: 1,
};
const btnGhost = {
  ...btnBase,
  background: '#f1f5f9',
  color: '#334155',
};
const btnPrimary = {
  ...btnBase,
  background: '#0f172a',
  color: 'white',
};

